// rules.jsx
import React, { useEffect, useState } from 'react';
import './rules.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initQuestions } from '../../functions/setUserQuestions';
import { fetchUserQuestions } from '../../functions/fetchUserQuestions';
import { fetchStageProgress } from '../../functions/fetchStageProgress';
import { sendReport, extractReportIdFromResponse, normalizeReportTimeValue, resolveReportsAuthToken } from '../../functions/sendReport';
import { mergeReportId } from '../loginSlice';
import { setHealthSprintStageContext } from '../../functions/healthSprintStageContext';
import { selectAdminToken } from '../../admin/sessionSlice';
import { fetchThemeData, processQuestions } from '../../admin/themeSlice';
import { setBackButtonUrl } from '../uiSlice';

/** Serialize processQuestions → initQuestions so concurrent DELETE/INSERT cannot leave 0 rows. */
let rulesQuestionsPipeline = Promise.resolve();

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status: themeStatus, data: themeData } = useSelector((state) => state.theme);
  const { status, user } = useSelector((state) => state.auth);
  const [redirect, setRedirect] = useState("/health-sprint");
  const adminToken = useSelector(selectAdminToken);
  /** True while creating `stages` row + loading question ids (insert happens here, not on game page). */
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!adminToken) return;
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch, adminToken]);

  useEffect(() => {
    if (!user?.userId || !adminToken) {
      setIsLoading(false);
      return;
    }
    if (themeStatus === 'failed') {
      setIsLoading(false);
      return;
    }
    if (themeStatus !== 'succeeded' || !themeData?.themename) {
      setIsLoading(true);
      return;
    }

    let cancelled = false;
    const themeName = String(themeData.themename).trim();
    if (!themeName) {
      setIsLoading(false);
      return;
    }

    rulesQuestionsPipeline = rulesQuestionsPipeline
      .then(async () => {
        if (cancelled) return;
        setIsLoading(true);
        try {
          await dispatch(processQuestions(themeName)).unwrap();
          if (cancelled) return;
          const res = await initQuestions({
            userId: user.userId,
            email: user.email,
            fullName: user.name,
            themeName,
            organizationId: user.organizationId,
            sessionId: user.sessionId,
            token: adminToken,
            user_type: user.role,
            reportid: user.reportId || null,
          });
          if (cancelled) return;

          const stageId = res?.insertId != null ? res.insertId : null;
          const questionIds = Array.isArray(res?.questionIds) ? res.questionIds : [];
          if (stageId != null) {
            setHealthSprintStageContext({ stageId, questionIds, themeName });
          }

          try {
            if (stageId != null && user?.userId) {
              const prog = await fetchStageProgress({
                userId: user.userId,
                token: adminToken,
                stageId,
              });
              const st = prog?.stage;
              const pointsDb =
                st?.points != null && !Number.isNaN(Number(st.points)) ? Number(st.points) : 0;
              const timeRaw =
                st?.time != null && String(st.time).trim() !== ''
                  ? String(st.time).trim()
                  : '00:00:00';
              const timeSec = normalizeReportTimeValue(timeRaw);
              const userType =
                st?.user_type != null && String(st.user_type).trim() !== ''
                  ? String(st.user_type).trim()
                  : String(user.role || '');
              const uid = user.userId != null ? String(user.userId).trim() : '';
              const ridBad =
                user.reportId != null &&
                uid !== '' &&
                String(user.reportId).trim() === uid;
              const reportPayload = {
                sessionId: user.sessionId,
                role: user.role,
                token: resolveReportsAuthToken(user.token, adminToken),
                name: user.name,
                email: user.email || null,
                userId: user.userId,
                gameId: user.gameId || '',
                organizationId: user.organizationId,
                points: pointsDb,
                time: timeSec,
                reportId: ridBad ? null : user.reportId || null,
                ans: '[]',
                user_type: userType,
                owner: userType,
              };
              const sres = await sendReport(reportPayload);
              const rid = extractReportIdFromResponse(sres, user.userId);
              if (rid == null || rid === '') {
                console.warn('Rules: sendReport returned no reportId for stages sync', {
                  role: user.role,
                  rawResponse: sres,
                });
              }
              if (rid != null && rid !== '') {
                dispatch(mergeReportId(rid));
                try {
                  await initQuestions({
                    userId: user.userId,
                    email: user.email,
                    fullName: user.name,
                    themeName,
                    organizationId: user.organizationId,
                    sessionId: user.sessionId,
                    token: adminToken,
                    user_type: user.role,
                    reportid: rid,
                  });
                } catch (syncErr) {
                  console.warn('Rules: sync stages.reportid after report API', syncErr);
                }
              }
            }
          } catch (repErr) {
            console.warn(
              'Rules: initial report sync',
              repErr?.response?.data ?? repErr?.message ?? repErr
            );
          }

          await fetchUserQuestions({
            userId: user.userId,
            token: adminToken,
            themeName,
            questionIds,
          });
        } catch (err) {
          console.error('Rules: process → init → questions failed:', err);
          if (!cancelled) {
            const msg =
              typeof err === 'string'
                ? err
                : err?.payload ??
                  err?.message ??
                  'Could not prepare questions for this session.';
            void Swal.fire({
              icon: 'error',
              title: 'Questions',
              text: String(msg),
            });
          }
        } finally {
          setIsLoading(false);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    themeData?.themename,
    user?.userId,
    user?.organizationId,
    user?.sessionId,
    user?.email,
    user?.name,
    user?.role,
    user?.token,
    user?.gameId,
    adminToken,
    dispatch,
  ]);

  useEffect(() => {
    if (!themeData) return;
    
    setRedirect("/health-sprint");
      }, [themeData]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?save=true"));
  }, [status, user]);

  const handleNextClick = (e) => {
    e.preventDefault();
    navigate(redirect);
  };

  if (isLoading) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <>
      <div className="user-rules-container">
        <div className="user-rules-top">
          <img
            src={process.env.REACT_APP_S3_PATH + themeData?.logo}
            alt="Health Sprint"
            className="user-rules-image"
          />
        </div>
        <div className="user-rules-bottom">
          <div className="user-rules-big-title">{themeData?.main_title}</div>
          <div className="user-rules-subline">
            {themeData?.landing_page_title}
          </div>
          <div
  className="user-rules-heading"
  style={{
    backgroundColor: themeData?.box_bg_color || "#ffffff",
    color: themeData?.box_text_color || themeData?.button_text_color || "#000"
  }}
>
  How to Play
</div>
          <ul className="user-rules-list">
  {
    themeData?.rules.map((rules, index) => (
      <li
        key={index}
        style={{ color: themeData?.heading_text_color || "#000" }}
      >
        <i
          className="fa fa-arrow-right user-rule-icon"
          aria-hidden="true"
        />
        {rules}
      </li>
    ))
  }
</ul>
          <div style={{ textAlign: "center" }}>
           <button
  style={{
    backgroundColor: themeData?.box_bg_color || "#ffffff",
    color: themeData?.box_text_color || themeData?.button_text_color || "#000"
  }}
  className="user-next-button"
  onClick={handleNextClick}
>
              Next
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default UserRules;